import { useRouteContext } from "@tanstack/react-router";
import { ComponentProps, createContext, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { CheckInCreateCall, driverCheckInServices } from "@/domain";
import { emitWebSocketEvent } from "@/emitWebSocketEvent";
import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { QueueByCompany, QueueCompany, useGetAttendancesTodayByCompanyQuery, useGetQueueDelayStatusByCompanyQuery } from "@/store/apis/nextcargo";

type Actions = "call" | "shipping";
export type TabActionsContextV2Props = {
  handleCheckInCall: (data: CheckInCreateCall) => Promise<void>;
  handleShipping: (vehicle: QueueCompany) => void;
  queues?: QueueByCompany["result"];
  actions?: Actions[] | Actions;
  search?: {
    onClear: () => void;
    onChange: (value: string) => void;
    value: string;
  };
  filterFn?: ((item: QueueCompany, index: number, arr: QueueCompany[]) => boolean) | Array<number>;
};
export const ExpeditionTabContextV2 = createContext({} as TabActionsContextV2Props);
type ExpeditionTabProviderProps = {
  children: React.ReactNode;
} & Pick<TabActionsContextV2Props, "actions" | "queues" | "search" | "filterFn">;
export function ExpeditionTabProviderV2({ children, queues, filterFn, ...ctxValues }: ExpeditionTabProviderProps) {
  const { expedition } = useSession();
  const companyId = Number(expedition?.company.id);

  const apiOptions = {
    args: {
      id: companyId
    },
    config: {
      skip: !companyId
    }
  };
  const { data: attendanceData } = useGetAttendancesTodayByCompanyQuery(apiOptions.args, apiOptions.config);
  const { data: delay } = useGetQueueDelayStatusByCompanyQuery(apiOptions.args, apiOptions.config);
  // console.log(delay);

  const attendance = Number(attendanceData?.isInAttendance);
  const delayStatus = !!delay?.active;
  const shippingCompanyId = useRouteContext({ from: "__root__" }).shippingCompanyId;

  const role = expedition?.role;
  const isExpeditionViewer = role === "Espectador";
  const isConciergeViewer = role === "Portaria";

  const [search, setSearch] = useState(ctxValues.search?.value || "");

  const handleAttendanceLogic = (addQueueToAttendance: () => void) => {
    const canStartAttendance = (+attendance! && !delayStatus) || shippingCompanyId;

    if (canStartAttendance) {
      addQueueToAttendance();
    } else if (attendance && delayStatus) {
      toast.error("Por favor, cancelar o atraso!");
    } else {
      toast.error("Por favor, iniciar o atendimento da empresa!");
    }
  };

  // Função para lidar com a chamada do motorista via websocket
  const handleCheckInCall = async (data: CheckInCreateCall) => {
    try {
      const checkinCall = await driverCheckInServices.createCall(data);
      const { driver, truck } = checkinCall.queue;

      // Emite o evento do WebSocket e desconecta automaticamente
      await emitWebSocketEvent("createCheckInCall", checkinCall);

      toast.success(`Motorista chamado ${driver.name} ${truck.licensePlate}`);
    } catch (error) {
      console.log(error);
      toast.error("Erro ao fazer o chamado");
    }
  };

  // Função para lidar com o carregamento de veículos
  const handleShipping = (vehicle: QueueCompany) => {
    if (!queues) return;
    const queue = queues.original.find((item) => item.id === vehicle.id);

    if (!queue) return;

    // Função auxiliar para adicionar queue ao atendimento
    const addQueueToAttendance = () => {
      MODAL_HANDLER.expeditions.shipment({ queue });
    };

    // Lógica para visualizador de expedição
    if (isExpeditionViewer) {
      addQueueToAttendance();
      return;
    }

    // Lógica para outros usuários baseada nas condições de atendimento
    handleAttendanceLogic(addQueueToAttendance);
  };

  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const filteredQueues = useMemo(() => {
    if (!queues) {
      return queues;
    }

    const normalizedSearch = normalize(search);

    const applyFilterFn = (list: QueueCompany[]) => {
      if (Array.isArray(filterFn)) {
        return list.filter((item) => !filterFn.includes(Number(item.id)));
      }

      if (typeof filterFn === "function") {
        return list.filter((item, index, arr) => filterFn(item, index, arr));
      }

      return list;
    };

    const matches = (queue: QueueCompany) => {
      const licensePlate = normalize(queue.truck.licensePlate).replace(/[^a-z0-9]/g, "");
      const driverName = normalize(queue.driver.name);
      const query = normalizedSearch.replace(/[^a-z0-9]/g, "");

      return licensePlate.includes(query) || driverName.includes(normalizedSearch);
    };

    const applySearch = (list: QueueCompany[]) => {
      if (!normalizedSearch) {
        return list;
      }

      return list.filter(matches);
    };

    const original = applySearch(applyFilterFn(queues.original));
    const grouped = queues.grouped
      .map((group) => {
        const groupQueues = applySearch(applyFilterFn(group.queues));

        return {
          ...group,
          queues: groupQueues,
          length: groupQueues.length
        };
      })
      .filter((group) => group.length > 0);

    return {
      ...queues,
      original,
      grouped
    };
  }, [queues, search, filterFn]);

  const handleSearch = (value: string) => {
    setSearch(value);
    ctxValues.search?.onChange(value);
  };

  const handleClear = () => {
    setSearch("");
    ctxValues.search?.onClear();
  };

  return (
    <ExpeditionTabContextV2.Provider
      value={{
        ...ctxValues,
        handleCheckInCall,
        handleShipping,
        search: {
          onClear: handleClear,
          onChange: handleSearch,
          value: search
        },
        queues: filteredQueues
      }}
    >
      {children}
    </ExpeditionTabContextV2.Provider>
  );
}

type ExpeditionTabImplProps = {
  children?: React.ReactNode;
} & Omit<ComponentProps<typeof ExpeditionTabContextV2.Provider>["value"], "handleCheckInCall" | "handleShipping">;
export function ExpeditionTabImpl({ children, ...ctxValues }: ExpeditionTabImplProps) {
  return <ExpeditionTabProviderV2 {...ctxValues}>{children}</ExpeditionTabProviderV2>;
}
